import type { MouseEvent } from 'react';
import { Component } from 'react';

import isEqual from 'lodash/isEqual';
import { LocalStorage } from 'saddlebag-localstorage';

import BpkAutosuggest, {
  BpkAutosuggestSuggestion,
} from '@skyscanner/backpack-web/bpk-component-autosuggest';
import { CLEAR_BUTTON_MODES } from '@skyscanner/backpack-web/bpk-component-input';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { RESULT_TYPE } from '../../utils/constants';
import {
  buildAdditionalInfoAutoSuggestPoiClick,
  buildControlsActivatedMessage,
  buildControlsDismissedMessage,
  buildResultSelectedMessage,
} from '../../utils/mini-events';
import FormField from '../FormField';
import {
  getSubheadingValue,
  getSuggestionIcon,
  getPuredItem,
  pureSuggestValue,
  getSuggestionReplacedValue,
} from '../searchControlHelper';

import PlaceHolder from './PlaceHolder';
import RecentSearchAndPopulars from './RecentSearchAndPopulars';
import RecentSearchAndPopularsMobile from './RecentSearchAndPopularsMobile';

import type {
  AutosuggestSuggestion,
  AutosuggestSuggestionsOrPopularDestinations,
  PopularDestination,
  AutosuggestPoi,
} from '../../types';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { Maybe } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';

import STYLES from './DestinationSelector.module.scss';

import {
  CONTROLS_ACTIVATED,
  CONTROLS_DISMISSED,
  RESULT_SELECTED,
} from '@shared/constants/newrelic-events/event-names';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import logger from '@shared/utils-frontend/logger';
import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';

const cls = cssModules(STYLES);

const renderSuggestion = (suggestion: AutosuggestSuggestion) => (
  <BpkAutosuggestSuggestion
    value={getSuggestionReplacedValue(suggestion)}
    subHeading={getSubheadingValue(suggestion, true)}
    icon={getSuggestionIcon(suggestion)}
    data-test-id="autosuggest-suggestion"
    tertiaryLabel={suggestion.label}
  />
);

const buildDestinationState = (props: Props) => {
  const { entity: destinationName } = props.destination || {};

  return {
    value: destinationName || '',
  };
};

const hasSuggestions = (
  suggestions: AutosuggestSuggestionsOrPopularDestinations,
) => suggestions && suggestions.length;

const isCityLevel = (destination: any) => destination?.type === 'City';

type Props = {
  i18n: I18nService;
  onSuggestionSelected: Function;
  onSuggestionsFetchRequested: Function;
  onSuggestionsClearRequested: Function;
  suggestions: AutosuggestSuggestionsOrPopularDestinations;
  destination?: Destination;
  destinationLabel?: string;
  className?: Maybe<string>;
  lightLabel?: boolean;
  disabled?: boolean;
  arrangeInline?: boolean;
  masonry?: boolean;
  isMobile?: boolean;
  onChangeInputValue?: Maybe<Function>;
  valid?: Maybe<boolean>;
  miniEventTracker: MiniEventTracker;
  hideLabel?: boolean;
};

type State = {
  value: string;
  visible: boolean;
  lastDisplayed: Maybe<AutosuggestSuggestion | PopularDestination>;
  prevProps: Props;
};

const defaultProps = {
  destinationLabel: undefined,
  className: null,
  lightLabel: false,
  disabled: false,
  arrangeInline: false,
  valid: false,
  isMobile: false,
};

const destinationIsChanging = (nextProps: Props, prevState: State) => {
  const { prevProps } = prevState;
  const { destination: nextDestination } = nextProps;
  const { entity: nextEntity, entityId: nextEntityId } = nextDestination || {};

  const { destination } = prevProps;
  const { entity, entityId } = destination || {};

  return entityId !== nextEntityId || entity !== nextEntity;
};

const localStorage = new LocalStorage('hotelsWebsiteLocalStorage');

class DestinationSelector extends Component<Props, State> {
  static defaultProps = defaultProps;

  highlightedSuggestion: Maybe<AutosuggestSuggestion | AutosuggestPoi>;

  isFocused: boolean;

  inputRef: Maybe<HTMLInputElement>;

  constructor(props: Props) {
    super(props);

    this.state = {
      ...buildDestinationState(props),
      lastDisplayed: null,
      prevProps: props,
      visible: false,
    };

    this.highlightedSuggestion = null;
    this.isFocused = true;
    this.inputRef = null;

    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSuggestionHighlighted = this.onSuggestionHighlighted.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
  }

  componentDidMount() {
    setTimeout(() => {
      if (this.inputRef) {
        this.inputRef.focus();
      }
    }, 0);
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (destinationIsChanging(nextProps, prevState)) {
      const newState = { ...prevState };
      const { entity: destinationName } = nextProps.destination || {};

      newState.value = destinationName || '';
      newState.prevProps = nextProps;

      const { suggestions } = nextProps;
      if (hasSuggestions(suggestions)) {
        const [lastDisplayed] = suggestions;
        newState.lastDisplayed = lastDisplayed;
      }

      return newState;
    }

    return null;
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  onRefCallback = (element: Maybe<HTMLInputElement>) => {
    if (element) {
      this.inputRef = element;
    }
  };

  onChange(
    _e: Event,
    { newValue }: { newValue: string },
    visible: boolean = false,
  ) {
    this.setState({
      value: newValue,
      visible,
    });
  }

  onClear() {
    this.setState({
      value: '',
    });

    if (this.props.onChangeInputValue) {
      this.props.onChangeInputValue('');
    }
  }

  onFocus = (event: Event) => {
    const {
      arrangeInline,
      destination,
      i18n: { culture },
      miniEventTracker,
    } = this.props;
    if (arrangeInline) {
      const target: any = event.currentTarget;
      const { value } = target;
      if (
        value.length > 0 &&
        value.slice(-1) !== ' ' &&
        isCityLevel(destination)
      ) {
        this.onChange(event, { newValue: `${value} ` }, true);
      } else {
        setTimeout(() => target.select(), 10);
      }
    }

    const prefilledQuery = this.state.value;
    const isRecentIncluded = !!(
      (!prefilledQuery || prefilledQuery.length === 0) &&
      JSON.parse(localStorage.tryGetValue('recentSearchItems'))
    );

    const query = {
      prefilledQuery,
      isRecentIncluded,
    };

    const message = buildControlsActivatedMessage(query, culture);
    miniEventTracker.trackAutoSuggest(message);
    logger.event(CONTROLS_ACTIVATED);
  };

  onBlur(
    _event: MouseEvent,
    {
      highlightedSuggestion,
    }: {
      highlightedSuggestion: Maybe<AutosuggestSuggestion | AutosuggestPoi>;
    },
  ) {
    const {
      i18n: { culture },
      miniEventTracker,
    } = this.props;

    if (!highlightedSuggestion) {
      const query = this.state.value;
      const message = buildControlsDismissedMessage(query, culture);
      miniEventTracker.trackAutoSuggest(message);
      logger.event(CONTROLS_DISMISSED);
    }

    this.autoSelectSuggestion();
  }

  onKeyUp = (e: any) => {
    const { value } = this.state;
    const { destination } = this.props;
    if (
      e.key === 'Backspace' &&
      value.slice(-1) !== ' ' &&
      isCityLevel(destination) &&
      destination?.entity === value
    ) {
      e.currentTarget.select();
    }
  };

  onSuggestionHighlighted({
    suggestion,
  }: {
    suggestion: AutosuggestSuggestion;
  }) {
    this.highlightedSuggestion = suggestion;
  }

  onSuggestionSelected(e: Event | {}, item: object) {
    if ((e as Event).preventDefault) {
      (e as Event).preventDefault();
    }

    const {
      i18n: { culture },
      miniEventTracker,
      onSuggestionSelected,
    } = this.props;

    const puredItem = getPuredItem(item);
    this.onSuggestionHighlighted(puredItem);
    onSuggestionSelected(puredItem);

    if (puredItem.method) {
      const {
        isFromRecentSearch,
        isPopularDestination,
        isSubItem,
        suggestionIndex,
        suggestionValue,
      } = puredItem;
      const { entityId } = puredItem.suggestion;
      const query = this.state.value;
      const searchType =
        (isFromRecentSearch && RESULT_TYPE.RECENT) ||
        (isPopularDestination && RESULT_TYPE.POPULAR) ||
        (isSubItem && RESULT_TYPE.SUBITEM) ||
        RESULT_TYPE.AUTO_SUGGEST;
      const message = buildResultSelectedMessage(
        query,
        suggestionValue,
        suggestionIndex,
        entityId,
        culture,
        searchType,
      );
      miniEventTracker.trackAutoSuggest(message);
      logger.event(RESULT_SELECTED);
      miniEventTracker.trackSmartMetric('hotel_destination_selected');
    }
  }

  autoSelectSuggestion() {
    const { arrangeInline, destination, isMobile, suggestions } = this.props;
    const { lastDisplayed, value } = this.state;
    const isCityLevelArrangeInline = arrangeInline && isCityLevel(destination);
    let valueNew = value;
    if (hasSuggestions(suggestions)) {
      let suggestion =
        this.highlightedSuggestion || suggestions[0] || lastDisplayed;
      if (!value) {
        suggestion = this.highlightedSuggestion;
      }
      valueNew =
        // @ts-expect-error Property 'entity' does not exist on type 'PopularDestination'.
        suggestion && suggestion.entity ? pureSuggestValue(suggestion) : '';
      if (!isMobile) {
        const item = { suggestion };
        this.onSuggestionSelected({}, item);
        if (isCityLevelArrangeInline && valueNew.slice(-1) === ' ') {
          valueNew = valueNew.trim();
        }
      }
    } else if (isCityLevelArrangeInline && value.slice(-1) === ' ') {
      valueNew = value.trim();
    }
    this.setState({
      visible: false,
      value: valueNew,
    });
  }

  renderRecentAndPopulars = (suggestion: PopularDestination) => {
    const { onSuggestionsClearRequested } = this.props;
    return (
      <RecentSearchAndPopulars
        suggestion={suggestion}
        onSuggestionSelected={this.onSuggestionSelected}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onBlur={this.onBlur}
      />
    );
  };

  renderRecentAndPopularsMobile = (suggestion: PopularDestination) => (
    <RecentSearchAndPopularsMobile
      suggestion={suggestion}
      onSuggestionSelected={this.onSuggestionSelected}
      onBlur={this.onBlur}
    />
  );

  renderPoi = (
    cityId: string,
    index: number,
    poi: AutosuggestPoi,
    onSuggestionsClearRequested: Function,
  ) => {
    const clickPoi = (e: MouseEvent) => {
      e.stopPropagation();
      this.onSuggestionSelected(
        {},
        {
          suggestion: poi,
          suggestionIndex: index,
          suggestionValue: poi.entity,
          method: 'click',
          isSubItem: true,
        },
      );
      this.onBlur(e, { highlightedSuggestion: poi });
      onSuggestionsClearRequested();
      const { miniEventTracker } = this.props;
      miniEventTracker.trackHotelsAction(
        ACTION_TYPE.AUTO_SUGGEST_POIS_CLICK,
        buildAdditionalInfoAutoSuggestPoiClick({ poiId: poi.entityId, cityId }),
      );
    };

    return (
      <div
        className={cls('DestinationSelector__suggestion--poi')}
        key={poi.entityId}
        role="button"
        onKeyUp={() => {}}
        tabIndex={0}
        onClick={clickPoi}
      >
        {poi.entity}
      </div>
    );
  };

  renderSuggestionIncludePOI = (suggestion: AutosuggestSuggestion) => {
    const { onSuggestionsClearRequested } = this.props;
    const { entityId } = suggestion;
    const content =
      suggestion.pois &&
      suggestion.pois.length > 0 &&
      suggestion.pois.map((poi, index) =>
        this.renderPoi(entityId, index, poi, onSuggestionsClearRequested),
      );

    return (
      <section
        className={cls('DestinationSelector__section')}
        data-test-id="autosuggest-suggestion"
      >
        <BpkAutosuggestSuggestion
          value={getSuggestionReplacedValue(suggestion)}
          subHeading={getSubheadingValue(suggestion, true)}
          icon={getSuggestionIcon(suggestion)}
          data-test-id="autosuggest-suggestion"
          className={cls('DestinationSelector__suggestion')}
          tertiaryLabel={suggestion.label}
        />
        {suggestion.pois && suggestion.pois.length > 0 && (
          <div className={cls('DestinationSelector__suggestion--pois')}>
            {content}
          </div>
        )}
      </section>
    );
  };

  render() {
    const {
      arrangeInline,
      className,
      destinationLabel,
      disabled,
      hideLabel,
      i18n: { translate },
      isMobile,
      lightLabel,
      masonry,
      onSuggestionsClearRequested,
      onSuggestionsFetchRequested,
      suggestions,
      valid,
    } = this.props;
    const { value, visible } = this.state;

    const hasPopular =
      suggestions[0] &&
      (suggestions[0] as PopularDestination).popularDestinations &&
      (suggestions[0] as PopularDestination).popularDestinations.length > 0;

    const inputId = `destination-autosuggest${masonry ? '-masonry' : ''}`;
    const inputClassName = cls('DestinationSelector__input');
    const clearButtonMode = arrangeInline
      ? CLEAR_BUTTON_MODES.never
      : CLEAR_BUTTON_MODES.whileEditing;

    const inputProps = {
      className: inputClassName,
      id: inputId,
      'data-testid': 'destination-autosuggest',
      name: inputId,
      placeholder: translate('SearchControls_label_Destination_placeholder'),
      value,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onKeyUp: this.onKeyUp,
      disabled,
      autoComplete: 'off',
      autoCorrect: 'off',
      autoCapitalize: 'off',
      spellCheck: 'false',
      clearButtonMode,
      clearButtonLabel: translate('Button_clear_input'),
      onClear: this.onClear,
      large: isMobile,
      valid,
    };

    const shouldRenderPopular = hasPopular && !value;
    const originalSuggestions = hasPopular ? [] : suggestions;
    const showSuggestions = shouldRenderPopular
      ? suggestions
      : originalSuggestions;

    const extraProps = isMobile
      ? {
          renderSuggestion: shouldRenderPopular
            ? this.renderRecentAndPopularsMobile
            : renderSuggestion,
          inputProps: { ...inputProps, inputRef: this.onRefCallback },
          alwaysRenderSuggestions: true,
          theme: {
            suggestionsContainerOpen: cls(
              'DestinationSelector__suggestionsContainerOpen',
            ),
            suggestionsList: cls('DestinationSelector__suggestionsList'),
            suggestion: cls('DestinationSelector__suggestionItem'),
            suggestionHighlighted: cls(
              'DestinationSelector__suggestionItem--highlighted',
            ),
          },
        }
      : {
          renderSuggestion: shouldRenderPopular
            ? this.renderRecentAndPopulars
            : this.renderSuggestionIncludePOI,
          inputProps,
          shouldRenderSuggestions: () => this.isFocused,
          onSuggestionsClearRequested,
        };

    return (
      <FormField
        className={className}
        fieldId={inputId}
        label={destinationLabel}
        lightLabel={lightLabel}
        hideLabel={hideLabel}
      >
        <BpkAutosuggest
          suggestions={showSuggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          getSuggestionValue={pureSuggestValue}
          onSuggestionSelected={this.onSuggestionSelected}
          onSuggestionHighlighted={this.onSuggestionHighlighted}
          highlightFirstSuggestion
          focusInputOnSuggestionClick={false}
          includeIcon
          includeSubheading
          includeTertiaryLabel
          {...extraProps}
        />
        {visible && !isMobile && <PlaceHolder value={value} />}
      </FormField>
    );
  }
}

export default withMiniEventTracker(withI18n(DestinationSelector));
